import { FC, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, experimentalStyled, Typography } from "@mui/material";
import LoginLogo from "icons/LoginLogo";
import { LockOutlined } from "@mui/icons-material";
import MainLogoTight from "icons/MainLogoTight";
import LoginFormFormik from "./LoginFormFormik";
import { LoginSettingsConsumer, LoginSettingsProvider } from "contexts/LoginSettingsContext";
import SendEmailLinkFormFormik from "./SendEmailLinkFormFormik";
import ResetFormFormik from "./ResetFormFormik";
import NewPasswordRequiredFormFormik from "./NewPasswordRequiredFormFormik";
import SetUpTotpFormik from "./SetUpTotpFormik";
import useDialog from "hooks/useDialog";
import DisplayModal from "components/widgets/modals/DisplayModal";
import { ModalComponentState } from "interfaces/modal";

const LoginRoot = experimentalStyled("div")(({ theme }) => ({
    ...(theme.palette.mode === "light" && {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.primary.contrastText,
        minHeight: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    })
}));

const LoginContainer = experimentalStyled(Box)(({ theme }) => ({
    position: "relative",
    minHeight: 465,
    margin: "30px 0 80px",
    zIndex: 10
}));

const LoginPanel = experimentalStyled(Box)(({ theme }) => ({
    width: 360,
    margin: "0 auto",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderRadius: "6px",
    boxShadow: theme.shadows[1],
    transition: "top .3s",
    padding: "0 15px 40px",
    position: "relative"
}));

const LoginFooterContainer = experimentalStyled(Box)(({ theme }) => ({
    position: "absolute",
    zIndex: 1,
    [theme.breakpoints.up("md")]: {
        bottom: "110px"
    },
    [theme.breakpoints.down("md")]: {
        bottom: "10px"
    },
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary
}));

const Login: FC = () => {
    const [checked, setChecked] = useState<boolean>(false);

    const [createDialog, closeDialog] = useDialog();
    const onOpenTwiceConfirmDialog = (item: ModalComponentState): void => {
        createDialog({
            disabledBackDropClick: false,
            children: <DisplayModal {...item} />
        });
    };

    useEffect(() => {
        setChecked(true);
    }, []);

    return (
        <LoginRoot>
            <Helmet>
                <title>Login | Guardian</title>
            </Helmet>
            <LoginSettingsProvider>
                <LoginContainer>
                    <Box
                        sx={{
                            mb: 6,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>
                        <MainLogoTight
                            viewBox="0 0 169.814 46.354"
                            sx={{ width: "200px", height: "46.354px" }}
                        />
                    </Box>
                    <LoginPanel sx={{ top: checked ? 0 : 50 }}>
                        <Box
                            sx={{
                                minHeight: "105px",
                                position: "relative",
                                p: "25px 15px",
                                borderRadius: "4px",
                                top: "-35px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                background:
                                    "transparent linear-gradient(270deg, #00A0FF 0%, #3BB6FF 100%) 0% 0% no-repeat padding-box"
                            }}>
                            <LoginSettingsConsumer>
                                {({ settings }) => {
                                    return settings.currStep === "LOGIN" ? (
                                        <LockOutlined
                                            sx={{
                                                fontSize: "50px",
                                                color: "primary.contrastText"
                                            }}
                                        />
                                    ) : settings.currStep === "NEW_PASSWORD_REQUIRED" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "20px" }}
                                                color="primary.contrastText">
                                                New password required
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "12px", mt: 2 }}
                                                color="primary.contrastText">
                                                Update your default password
                                            </Typography>
                                        </Box>
                                    ) : settings.currStep === "MFA_SETUP" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "20px" }}
                                                color="primary.contrastText">
                                                Two-Factor setup
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "12px", mt: 2 }}
                                                color="primary.contrastText">
                                                Scan the Qrcode and then get one-time password in
                                                your TOTP-generating app (like Google
                                                Authenticator).
                                            </Typography>
                                        </Box>
                                    ) : settings.currStep === "SOFTWARE_TOKEN_MFA" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "20px" }}
                                                color="primary.contrastText">
                                                Two-Factor Authentication Code
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "12px", mt: 2 }}
                                                color="primary.contrastText">
                                                Enter the authentication code from your 2FA device.
                                                (like Google Authenticator).
                                            </Typography>
                                        </Box>
                                    ) : settings.currStep === "SEND_RESET_PASSWORD_LINK" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "20px" }}
                                                color="primary.contrastText">
                                                Reset your password
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "12px", mt: 2 }}
                                                color="primary.contrastText">
                                                Enter your email to receive a reset password link.
                                            </Typography>
                                        </Box>
                                    ) : settings.currStep === "RESET_PASSWORD" ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "20px" }}
                                                color="primary.contrastText">
                                                Reset your password
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "12px", mt: 2 }}
                                                color="primary.contrastText">
                                                If the email you entered is recognised you will
                                                receive an email shortly with a verification code,
                                                enter this code below. Check your junkmail box if
                                                you haven’t received the code in the next few
                                                minutes. If you still haven’t received an email
                                                click re-send email or contact us:
                                                <a
                                                    href="mailto:guardian@3dg.net.nz"
                                                    style={{
                                                        marginLeft: "5px",
                                                        color: "#fff",
                                                        textDecoration: "underline"
                                                    }}>
                                                    guardian@3dg.net.nz
                                                </a>
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <LockOutlined
                                            sx={{
                                                fontSize: "50px",
                                                color: "primary.contrastText"
                                            }}
                                        />
                                    );
                                }}
                            </LoginSettingsConsumer>
                        </Box>
                        <LoginSettingsConsumer>
                            {({ settings }) => {
                                return settings.currStep === "LOGIN" ? (
                                    <LoginFormFormik />
                                ) : settings.currStep === "NEW_PASSWORD_REQUIRED" ? (
                                    <NewPasswordRequiredFormFormik />
                                ) : settings.currStep === "MFA_SETUP" ? (
                                    <SetUpTotpFormik qrcode="qrcode" />
                                ) : settings.currStep === "SOFTWARE_TOKEN_MFA" ? (
                                    <SetUpTotpFormik />
                                ) : settings.currStep === "SEND_RESET_PASSWORD_LINK" ? (
                                    <SendEmailLinkFormFormik />
                                ) : settings.currStep === "RESET_PASSWORD" ? (
                                    <ResetFormFormik />
                                ) : (
                                    <LoginFormFormik />
                                );
                            }}
                        </LoginSettingsConsumer>
                    </LoginPanel>
                </LoginContainer>

                <LoginFooterContainer>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Button
                            size="small"
                            sx={{ color: "grey.A100", fontSize: 10, whiteSpace: "nowrap" }}
                            onClick={() => {
                                onOpenTwiceConfirmDialog({
                                    closeDialog,
                                    title: "TERMS OF USE"
                                });
                            }}>
                            TERMS OF USE
                        </Button>
                        <Button
                            size="small"
                            disableRipple
                            sx={{
                                color: "grey.A100",
                                fontSize: 10,
                                cursor: "default",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                    background: "transparent"
                                }
                            }}>
                            COPYRIGHT ® 2021-{new Date().getFullYear()} 3RDEGREE
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            mt: 1,
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            window.open("https://www.3dg.net.nz");
                        }}>
                        <LoginLogo sx={{ width: 148, height: 21 }} />
                    </Box>
                </LoginFooterContainer>
            </LoginSettingsProvider>
        </LoginRoot>
    );
};

export default Login;
